import Vue from 'vue';
import VueRouter from 'vue-router';
import { requiresCrmLogin, refreshCrmSession } from '@/api/crm-auth';

import liff from "@line/liff";
import { canAccessRoute, getFirstAccessibleRoute } from '@/utils/permission';
import AccountSettings from "@/pages/systemSettings/AccountSettings";
import Administrator from "@/pages/systemSettings/Administrator";
import AnalyticsIndex from "@/pages/analytics/AnalyticsIndex";
import Home from '@/pages/Home.vue';
import Login from '@/pages/Login';
import Notices from "@/pages/Notices";
import OnlineReserve from "@/pages/OnlineReserve";
import OpenaiConversation from "@/pages/OpenaiConversation";
import Patients from "@/pages/Patients";
import ProgressIndex from "@/pages/PostOperativeCare/Progress/Index.vue";
import PendingList from "@/pages/PostOperativeCare/Progress/PendingList.vue";
import HistoryList from "@/pages/PostOperativeCare/Progress/HistoryList.vue";
import Question from "@/pages/Question";
import Reschedule from "@/pages/Reschedule";
import SuperLink from "@/pages/SuperLink";
import Upload from "@/pages/Upload";
import Settings from "@/pages/settings/index";
import store from '@/store';
import { loginLiff } from "@/utils/tool";
import AICustomerService from '@/pages/AICustomerService';
import SwitchAccount from "@/pages/SwitchAccount.vue";
import LineSendStep from "@/pages/message/send/LineSendStep.vue";
import MessageIndex from "@/pages/message/MessageIndex.vue";
import SmsRefillLog from "@/pages/message/SmsRefillLog.vue";
import MessageLog from "@/pages/message/MessageLog.vue";
import MessageSetting from "@/pages/message/setting/MessageSetting.vue";
import LineSetting from "@/pages/message/setting/LineSetting.vue";
import SmsSetting from "@/pages/message/setting/SmsSetting.vue";
import MessengerSetting from "@/pages/message/setting/MessengerSetting.vue";
import VoiceSetting from "@/pages/message/setting/VoiceSetting.vue";
import OnlineAppointmentsSettingIndex from "@/pages/onlineAppointmentsSetting/OnlineAppointmentsSettingIndex.vue";
import FreeTime from "@/pages/onlineAppointmentsSetting/FreeTime.vue";
import TreatmentSetting from "@/pages/onlineAppointmentsSetting/TreatmentSetting.vue";
import ReviewIndex from "@/pages/review/ReviewIndex.vue";
import ReviewOverview from "@/pages/review/ReviewOverview.vue";
import ReviewSettings from "@/pages/review/setting/ReviewSettings.vue";
import ModeSetting from "@/pages/review/setting/ModeSetting.vue";
import AiSetting from "@/pages/review/setting/AiSetting.vue";
import CloseNotificationSetting from "@/pages/message/CloseNotificationSetting.vue";
import GoogleReviewPage from "@/pages/review/GoogleReviewPage.vue";
import MessageSendIndex from "@/pages/message/send/MessageSendIndex.vue";
import SmsSendStep from "@/pages/message/send/SmsSendStep.vue";
import ManualAppointmentNotification from "@/pages/message/send/ManualAppointmentNotification.vue";
import HealthEducationList from "@/pages/HealthEducation/HealthEducationList.vue";
import HealthEducationIndex from "@/pages/HealthEducation/HealthEducationIndex.vue";
import HealthEducationForm from "@/pages/HealthEducation/HealthEducationForm.vue";
import PostOperativeCareTemplateIndex from "@/pages/PostOperativeCare/Templates/Index.vue";
import PostOperativeCareTemplateList from "@/pages/PostOperativeCare/Templates/TemplateList.vue";
import PostOperativeCareTemplateForm from "@/pages/PostOperativeCare/Templates/TemplateForm.vue";

// Analytics Tab Components
import LineInviteAnalysis from "@/pages/analytics/LineInviteAnalysis.vue";
import PatientAnalysis from "@/pages/analytics/PatientAnalysis.vue";
import OnlineReserveAnalysisV2 from "@/pages/analytics/OnlineReserveAnalysisV2.vue";
import OnlineSummaryAnalysisTab from "@/components/analytics/OnlineSummaryAnalysisTab.vue";
import OnlineDetailAnalysisTab from "@/components/analytics/OnlineDetailAnalysisTab.vue";
import SystemSettingsIndex from "@/pages/systemSettings/SystemSettingsIndex.vue";
import SubAccountSettings from "@/pages/systemSettings/SubAccountSettings.vue";
import PhonePermissionSettings from "@/pages/systemSettings/PhonePermissionSettings.vue";

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(err => err)
}

Vue.use(VueRouter);

const inLiff = liff.isInClient()
const liffAuth = sessionStorage.getItem('liffAuth') !== '0'

const router = new VueRouter({
  mode: 'history',
  linkExactActiveClass: 'active',
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: {
        auth: true,
        liff: true,
        canSpecial: false
      },
      beforeEnter: (to, from, next) => {
        if (store.getters.isSpecialClinic)
          return router.push({name: 'onlineReserve'}).catch(() => {
          })
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_RESERVE_LIFF, next)
        } else {
          next()
        }
      }
    },
    {
      path: '/login',
      name: 'login',
      component: Login
    },
    {
      path: '/switch-account',
      name: 'switchAccount',
      component: SwitchAccount,
      meta: {
        auth: true,
        admin: true
      }
    },
    {
      path: '/online-reserve',
      name: 'onlineReserve',
      component: OnlineReserve,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true
      },
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_ONLINE_RESERVE_LIFF, next)
        } else {
          next()
        }
      }
    },
    {
      path: '/reschedule',
      name: 'reschedule',
      component: Reschedule,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true
      },
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_RESCHEDULE_LIFF, next)
        } else {
          next()
        }
      }
    },
    {
      path: '/patients',
      name: 'patients',
      component: Patients,
      meta: {
        auth: true,
        canSpecial: false
      }
    },
    {
      path: '/free-time',
      name: 'freeTime',
      component: OnlineAppointmentsSettingIndex,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true
      },
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_FREE_TIME_LIFF, next)
        } else {
          next()
        }
      },
      redirect: {name: 'freeTimeSetting'},
      children: [
        {
          path: 'free-time-setting',
          name: 'freeTimeSetting',
          component: FreeTime,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'treatment-setting',
          name: 'treatmentSetting',
          component: TreatmentSetting,
          meta: {
            auth: true,
            canSpecial: true
          }
        }
      ]
    },
    {
      path: '/review',
      name: 'review',
      component: ReviewIndex,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true
      },
      redirect: {name: 'reviewList'},
      children: [
        {
          path: 'list',
          name: 'reviewList',
          component: ReviewOverview,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'google-review-list',
          name: 'googleReviewList',
          component: GoogleReviewPage,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'setting',
          component: ReviewSettings,
          name: 'reviewSettings',
          redirect: {name: 'reviewSettingMode'},
          children: [
            {
              path: 'mode',
              name: 'reviewSettingMode',
              component: ModeSetting,
              meta: {
                auth: true,
                canSpecial: true
              }
            },
            {
              path: 'ai',
              name: 'aiSettingMode',
              component: AiSetting,
              meta: {
                auth: true,
                canSpecial: true
              }
            }
          ]
        }
      ],
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_REVIEW_LIFF, next)
        } else {
          next()
        }
      }
    },
    {
      path: '/upload',
      name: 'upload',
      component: Upload,
      meta: {
        auth: true,
        canSpecial: true
      }
    },
    {
      path: '/settings/info',
      name: 'settings',
      component: Settings,
      meta: {
        auth: true,
        canSpecial: true
      }
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: AnalyticsIndex,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true
      },
      redirect: '/analytics/patient-recommend',
      children: [
        {
          path: 'patient-recommend',
          name: 'analyticsPatientRecommend',
          component: LineInviteAnalysis,
          meta: { auth: true, canSpecial: true }
        },
        {
          path: 'online-reserve',
          name: 'analyticsOnlineReserve',
          component: OnlineReserveAnalysisV2,
          meta: { auth: true, canSpecial: true },
          redirect: { name: 'analyticsOnlineReserveSummary' },
          children: [
            {
              path: 'summary',
              name: 'analyticsOnlineReserveSummary',
              component: OnlineSummaryAnalysisTab,
              meta: { auth: true, canSpecial: true }
            },
            {
              path: 'detail',
              name: 'analyticsOnlineReserveDetail',
              component: OnlineDetailAnalysisTab,
              meta: { auth: true, canSpecial: true }
            }
          ]
        },
        {
          path: 'patient-analysis',
          name: 'analyticsPatientAnalysis',
          component: PatientAnalysis,
          meta: { auth: true, canSpecial: true }
        }
      ],
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_ANALYSIS_LIFF, next)
        } else {
          next()
        }
      }
    },
    {
      path: '/settings/admin',
      name: 'adminSettings',
      component: Administrator,
      meta: {
        auth: true,
        canSpecial: true
      }
    },
    {
      path: '/settings/account',
      name: 'accountSettings',
      component: AccountSettings,
      meta: {
        auth: true,
        canSpecial: true
      }
    },
    {
      path: '/settings',
      name: 'systemSettingsIndex',
      component: SystemSettingsIndex,
      meta: {
        auth: true,
        canSpecial: true
      },
      beforeEnter: (to, from, next) => {
        // 方案 2：根據用戶類型動態 redirect
        const user = store.state.user
        if (to.path === '/settings') {
          if (user.type === '1') {
            // clinicUser 導向手機權限設定
            next('/settings/phone-permission')
          } else {
            // doctorUser 導向子帳號設定
            next('/settings/sub-accounts')
          }
        } else {
          next()
        }
      },
      children: [
        {
          path: 'phone-permission',
          name: 'PhonePermissionSettings',
          component: PhonePermissionSettings,
          meta: { auth: true, canSpecial: true },
          beforeEnter: (to, from, next) => {
            // 方案 3：權限守衛 - 只有 clinicUser 能訪問
            const user = store.state.user
            if (user.type === '1') {
              next()
            } else {
              // 醫師無權訪問，重定向到子帳號設定
              next('/settings/sub-accounts')
            }
          }
        },
        {
          path: 'sub-accounts',
          name: 'SubAccountSettings',
          component: SubAccountSettings,
          meta: { auth: true, canSpecial: true }
        }
      ],
    },
    {
      path: '/message',
      name: 'message',
      component: MessageIndex,
      meta: {
        auth: true,
        canSpecial: true
      },
      redirect: {name: 'MessageSendIndex'},
      children: [
        {
          path: 'send',
          name: 'MessageSendIndex',
          component: MessageSendIndex,
          meta: {
            auth: true,
            canSpecial: true
          },
          redirect: {name: 'messageSend'},
          children: [
            {
              path: 'line',
              name: 'messageSend',
              component: LineSendStep,
              meta: {
                auth: true,
                canSpecial: true
              }
            },
            {
              path: 'sms',
              name: 'smsMessageSend',
              component: SmsSendStep,
              meta: {
                auth: true,
                canSpecial: true
              }
            },
            {
              path: 'manual-appointment-notification',
              name: 'manualAppointmentNotification',
              component: ManualAppointmentNotification,
              meta: {
                auth: true,
                canSpecial: true
              }
            }
          ]
        },
        {
          path: 'sms-refill-log',
          name: 'smsRefillLog',
          component: SmsRefillLog,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'setting',
          name: 'messageSetting',
          component: MessageSetting,
          meta: {
            auth: true,
            canSpecial: true
          },
          redirect: {name: 'lineSetting'},
          children: [
            {
              path: 'line',
              name: 'lineSetting',
              component: LineSetting,
              meta: {
                auth: true,
                canSpecial: true
              }
            },
            {
              path: 'sms',
              name: 'smsSetting',
              component: SmsSetting,
              meta: {
                auth: true,
                canSpecial: true
              }
            },
            {
              path: 'messenger',
              name: 'messengerSetting',
              component: MessengerSetting,
              meta: {
                auth: true,
                canSpecial: true
              }
            },
            {
              path: 'voice',
              name: 'voiceSetting',
              component: VoiceSetting,
              meta: {
                auth: true,
                canSpecial: true
              }
            }
          ]
        },
        {
          path: 'message-log',
          name: 'messageLog',
          component: MessageLog,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'close-reminders',
          name: 'closeNotificationSetting',
          component: CloseNotificationSetting,
          meta: {
            auth: true,
            canSpecial: true
          }
        }
      ]
    },
    {
      path: '/question',
      name: 'question',
      component: Question,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true
      },
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_QUESTION, next)
        } else {
          next()
        }
      }
    },
    {
      path: '/notice',
      name: 'notice',
      component: Notices,
      meta: {
        auth: true,
        canSpecial: true
      },
    },
    {  // 因為路徑大概，需要重新導向一下
      path: '/post-operative-care/',
      redirect: {name: 'careProgressUnprocessed'},
    },
    {
      path: '/post-operative-care/progress',
      name: 'operativeCare',
      component: ProgressIndex,
      meta: {
        auth: true,
        liff: true,
        canSpecial: true,
      },
      beforeEnter: (to, from, next) => {
        if (inLiff && liffAuth) {
          loginLiff(process.env.VUE_APP_POST_OPERATIVE_CARE_LIFF, next)
        } else {
          next()
        }
      },
      redirect: {name: 'careProgressUnprocessed'},
      children: [
        {
          path: 'pending',
          name: 'careProgressPending',
          component: PendingList,
          meta: { auth: true, canSpecial: true },
        },
        {
          path: 'unprocessed',
          name: 'careProgressUnprocessed',
          component: HistoryList,
          meta: { auth: true, canSpecial: true },
          props: { status: 0 },
        },
        {
          path: 'processed',
          name: 'careProgressProcessed',
          component: HistoryList,
          meta: { auth: true, canSpecial: true },
          props: { status: 1 },
        },
      ],
    },
    {
      path: '/superLink',
      name: 'superLink',
      component: SuperLink,
      meta: {
        auth: true,
        canSpecial: true
      },
    },
    {
      path: '/openaiConversation',
      name: 'openaiConversation',
      component: OpenaiConversation,
      meta: {
        auth: true,
        canSpecial: true
      },
    },
    {
      path: '/ai-customer-service',
      name: 'AICustomerService',
      component: AICustomerService,
      meta: {
        auth: true,
        canSpecial: true
      }
    },
    {
      path: '/health-education',
      name: 'healthEducation',
      meta: {
        auth: true,
        canSpecial: true
      },
      component: HealthEducationIndex,
      redirect: {name: 'HealthEducationList'},
      children: [
        {
          path: 'setting',
          name: 'HealthEducationList',
          component: HealthEducationList,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'new',
          name: 'HealthEducationNew',
          component: HealthEducationForm,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'edit/:id',
          name: 'HealthEducationEdit',
          component: HealthEducationForm,
          meta: {
            auth: true,
            canSpecial: true
          }
        }
      ]
    },
    {
      path: '/post-operative-care/templates',
      name: 'careTemplates',
      meta: {
        auth: true,
        canSpecial: true
      },
      component: PostOperativeCareTemplateIndex,
      redirect: { name: 'CareTemplateList' },
      children: [
        {
          path: 'list',
          name: 'CareTemplateList',
          component: PostOperativeCareTemplateList,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'new',
          name: 'CareTemplateNew',
          component: PostOperativeCareTemplateForm,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'edit/:id',
          name: 'CareTemplateEdit',
          component: PostOperativeCareTemplateForm,
          meta: {
            auth: true,
            canSpecial: true
          }
        },
        {
          path: 'schedule',
          name: 'CareTemplateSchedule',
          component: PendingList,
          meta: {
            auth: true,
            canSpecial: true
          }
        }
      ]
    },
    { // hint: Make sure it's the last
      path: '/:catchAll(.*)*',
      redirect: '/'
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const canLiff = to.matched.find(record => record.meta.liff)
  if (inLiff && canLiff && liffAuth) {
    await store.commit('setLiffUSer')
    next()
  } else {
    const loggedIn = localStorage.getItem('user')
    let user = JSON.parse(loggedIn)
    if(to.name !== 'login' && user && requiresCrmLogin(user)) {
      return next({name: 'login', query: {next: to.fullPath}})
    }

    if (to.matched.some(record => record.meta.auth) && !loggedIn) {
      next({name: 'login', query: {next: to.fullPath}})
      return
    } else if ('liff.state' in to.query && loggedIn) {
      const urlParams = new URLSearchParams(to.query['liff.state'])
      const type = urlParams.get('type')
      const clinicCode = urlParams.get('clinic_code') || ''
      const doctorCode = urlParams.get('doctor_code') || ''
      if (type === user.type && clinicCode === user.clinic_code && doctorCode === user.doctor_code) {
        store.commit('setUserData', user)
        // store.commit('userInfo/setPermissions', user?.permissions)
      } else {
        next({name: 'login', query: {next: to.path, type: type}})
        return
      }
    } else if (!Object.keys(store.state.user).length && loggedIn) {
      store.commit('setUserData', user)
      if (user.auth_mode === 'jwt' && to.name !== 'login') {
        try {
          const refreshed = await refreshCrmSession(store.state.user, () => store.state.user, session => {
            store.commit('setUserData', session)
            user = session
          })
          if (!refreshed) return next(false)
        } catch {
          store.dispatch('notify/showError', '登入身分暫時無法確認，請重新登入或稍後再試')
          return next({ name: 'login', query: { next: to.fullPath } })
        }
      }
      // store.commit('userInfo/setPermissions', user?.permissions)
    }

    // === 權限檢查：只有診所登入才檢查，醫師登入不受影響 ===
    if (loggedIn && to.matched.some(record => record.meta.auth)) {
      // 確保 store 已經載入 user 資料
      if (!Object.keys(store.state.user).length) {
        store.commit('setUserData', user)
      }

      // 醫師登入（type: "2"）不受權限系統影響，維持原本行為
      const isDoctorLogin = user.type === '2'
      const isSuperUser = user.plan === '99'

      if (!(isDoctorLogin || isSuperUser)) {
        // 診所登入才執行權限檢查
        if (!canAccessRoute(to)) {
          console.warn(`[Permission] 無權限訪問 ${to.name}，導回第一個有權限的頁面`)
          const redirectPath = getFirstAccessibleRoute()

          // 如果沒有任何可訪問的路由（權限為空），清除登入並導向登入頁
          if (redirectPath === null) {
            // 檢查是否為舊版本用戶（沒有 route_permissions 欄位）
            const isLegacyUser = user.route_permissions === undefined

            if (isLegacyUser) {
              // 舊版本用戶：靜默清除並導回登入（版本升級相容性處理）
              console.warn('[Permission] 檢測到舊版本用戶，清除快取並要求重新登入')
              localStorage.removeItem('user')
              next({ name: 'login', query: { next: to.fullPath } })
            } else {
              // 新版本用戶但沒有權限：顯示錯誤訊息
              console.error('[Permission] 用戶沒有任何頁面權限，強制登出')
              store.dispatch('notify/showAlert', '您的帳號沒有任何頁面權限，請聯絡管理員。')
              localStorage.removeItem('user')
              next({ name: 'login', query: { next: to.fullPath } })
            }
            return
          }

          next(redirectPath)
          return
        }
      }
    }

    if (from.name === 'login')
      await store.dispatch('afterLogin')
    next()
  }
})

// Sentry.init({
//   Vue,
//   dsn: process.env.VUE_APP_SENTRY_DSN,
//   integrations: [
//     new Integrations.BrowserTracing({
//       routingInstrumentation: Sentry.vueRouterInstrumentation(router),
//       tracingOrigins: ["localhost", process.env.VUE_APP_SENTRY_TRACING_ORIGIN, /^\//],
//     }),
//   ],
//   tracesSampleRate: 1.0,
//   sendDefaultPii: true
// });

export default router;
